import { useQuery, useMutation } from "@tanstack/react-query";
import { verificationApiService } from "./verification.api";
import { usePermission } from "context/PermissionContext";
import { PERMISSION_MODULES, PERMISSION_ACTIONS } from "common/constants/PermissionConstants";

export const useVerificationService = ({
  studyid = null,
  enabled = true,
}: {
  studyid?: number | null;
  enabled?: boolean;
} = {}) => {
  const { hasPermission } = usePermission();

  const SelectAllQuery = useQuery({
    queryKey: ["verification-queue-select-all", studyid],
    queryFn: () => verificationApiService.getSelectAllData(studyid),
    enabled: false,
  });

  const NotificationCountQuery = useQuery({
    queryKey: ["verification-notification-count", studyid],
    queryFn: () => verificationApiService.getSelectAllData(studyid),
    // enabled: !!studyid,
    enabled: false,
    select: (response: any): any => {
      const rawData = response?.data || {};
      let count = 0;

      const categories = [
        { key: "BLOCK", module: PERMISSION_MODULES.BLOCKED_VOLUNTEER, action: PERMISSION_ACTIONS.VERIFY },
        { key: "DROPOUT", module: PERMISSION_MODULES.DROPOUT_VOLUNTEER, action: PERMISSION_ACTIONS.VERIFY },
        { key: "TRANSFER", module: PERMISSION_MODULES.TRANSFER_VOLUNTEER, action: PERMISSION_ACTIONS.VERIFY },
        { key: "IP_INVENTORY", module: PERMISSION_MODULES.IP_INVENTORY, action: PERMISSION_ACTIONS.VERIFY },
        { key: "RETENTION", module: PERMISSION_MODULES.DRUG_RETENTION, action: PERMISSION_ACTIONS.VERIFY },
        { key: "DISPENSING", module: PERMISSION_MODULES.DISPENSING_LOG, action: PERMISSION_ACTIONS.VERIFY },
        { key: "RETURNS_LOG", module: PERMISSION_MODULES.RETURNS_LOG, action: PERMISSION_ACTIONS.VERIFY },
        { key: "DRUG_TRANSFER", module: PERMISSION_MODULES.DRUG_TRANSFER, action: PERMISSION_ACTIONS.VERIFY },
        { key: "DISPOSAL", module: PERMISSION_MODULES.DRUG_DISPOSAL, action: PERMISSION_ACTIONS.VERIFY },
        { key: "RECONCILIATION", module: PERMISSION_MODULES.STOCK_RECONCILIATION, action: PERMISSION_ACTIONS.VERIFY },
        { key: "ADVERSE_EVENT", module: PERMISSION_MODULES.ADVERSE_EVENT, action: PERMISSION_ACTIONS.VERIFY },
        { key: "AMBULATORY_VISIT", module: PERMISSION_MODULES.AMBULATORY_VISIT, action: PERMISSION_ACTIONS.VERIFY },
        { key: "LAB_TEST", module: `${PERMISSION_MODULES.STUDY_SCREENING}:LABTEST`, action: PERMISSION_ACTIONS.VERIFY },
      ];

      for (const cat of categories) {
        const items = rawData[cat.key];
        if (Array.isArray(items) && items.length > 0) {
          if (hasPermission(cat.module, cat.action)) {
            count += items.length;
          }
        }
      }

      return { data: count };
    },
  });

  return {
    SelectAllQuery,
    NotificationCountQuery,
  };
};

export const useSampleCollectionVerificationService = ({
  studyid,
  periodid,
  enabled = true,
}: {
  studyid: number;
  periodid: number;
  enabled?: boolean;
}) => {
  const SampleCollectionQuery = useQuery({
    queryKey: ["sample-collection-verification", studyid, periodid],
    queryFn: () => verificationApiService.getSampleCollectionData(studyid, periodid),
    enabled: enabled && !!studyid && !!periodid,
  });

  const GlobalVerifyMutation = useMutation({
    mutationFn: (payload: {
      studyid: number;
      periodid: number;
      verifiedby: number;
      verifypin: string;
    }) => verificationApiService.globalVerify(payload),
  });

  return {
    SampleCollectionQuery,
    GlobalVerifyMutation,
  };
};
